import type { ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SignIn as ClerkSignIn } from '@clerk/react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';

export function AuthPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="pt-18 flex flex-1 flex-col items-center justify-center px-4 pb-12 md:pt-20">
        {children}
      </main>
    </div>
  );
}

/**
 * Sign-in screen. Renders Clerk's prebuilt <SignIn /> component (hash routing,
 * the default for React SPAs) in place of the previous hand-rolled email-link /
 * phone-OTP flow, wrapped in the app's branded PageShell header. The auth
 * methods shown are controlled by the Clerk Dashboard. Theming comes from the
 * `--clerk-*` CSS variables defined in styles/globals.css.
 *
 * Post-sign-in redirect: on completion Clerk navigates to its fallback URL,
 * which drops the app's `redirect_to` query param. We forward that param via
 * `forceRedirectUrl` so the user lands on their intended destination; when it's
 * absent we send them to `/`, where SessionLoader routes them to their role's
 * home (and prevents a sign-in loop — see SessionLoader rule 5).
 */
export function SignIn() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to');
  const idleSignInEmail = (location.state as { idleSignInEmail?: unknown } | null)
    ?.idleSignInEmail;
  const emailHint =
    (typeof idleSignInEmail === 'string' ? idleSignInEmail : undefined) ??
    searchParams.get('email') ??
    searchParams.get('login_hint') ??
    undefined;

  return (
    <AuthPageShell>
      <ClerkSignIn
        appearance={{
          theme: 'simple',
          variables: {
            colorPrimary: 'var(--primary)',
            spacing: '1.25rem', // keeps the roomy gaps between rows
            borderRadius: '0.625rem',
            colorInput: '#EDF1FB',
            colorInputForeground: '#111827',
          },
          elements: {
            header: { gap: '1.3rem' },
            headerTitle: { fontSize: 'var(--text-xl)' },
            headerSubtitle: { fontSize: 'var(--text-sm)' },
            cardBox: { width: '446px' },
            card: { paddingLeft: '2rem', paddingRight: '2rem' },
            formButtonPrimary: {
              paddingTop: '0.85rem',
              paddingBottom: '0.85rem',
              borderRadius: '0.625rem',
              fontSize: '0.875rem',
              fontWeight: 600,
            },
            formFieldLabel: { fontSize: 'var(--text-sm)' },
            formFieldInputGroup: {
              height: '3rem', // the wrapper that was pinning it
            },
            formFieldInput: {
              height: '3rem !important',
              minHeight: '3rem !important',
              maxHeight: '3rem !important',
              paddingTop: 0,
              paddingBottom: 0,
              boxSizing: 'border-box',
              fontSize: '0.875rem',
            },
          },
        }}
        signUpUrl="/signup"
        {...(redirectTo ? { forceRedirectUrl: redirectTo } : {})}
        {...(emailHint ? { initialValues: { emailAddress: emailHint } } : {})}
      />
    </AuthPageShell>
  );
}
